import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PencilSimple, FloppyDisk, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import WOOCRPanel from "@/components/projects/WOOCRPanel";

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
      {hint && <div className="text-[10.5px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

const STATUSES = [
  { value: "planning", label: "Planning" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
];

/**
 * ProjectEditSheet — edit a project's core details PLUS Client Work Order
 * details (WO#, agreement, client GST/state, contractor GST/PAN, sub-project,
 * sub-head, site). All WO fields auto-flow into new BOQs and RA Bills created
 * against this project.
 */
export default function ProjectEditSheet({ project, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [woFile, setWoFile] = useState(null);
  const [woUploading, setWoUploading] = useState(false);

  useEffect(() => {
    if (open && project) {
      setForm({
        name: project.name || "",
        client_name: project.client_name || "",
        location: project.location || "",
        status: project.status || "planning",
        budget: project.budget || 0,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        description: project.description || "",
        // Work Order
        work_order_no: project.work_order_no || "",
        work_order_date: project.work_order_date || "",
        work_order_value: project.work_order_value || 0,
        agreement_no: project.agreement_no || "",
        name_of_work: project.name_of_work || "",
        sub_project: project.sub_project || "",
        sub_head: project.sub_head || "MEP",
        site: project.site || project.location || "",
        client_gstin: project.client_gstin || "",
        client_state: project.client_state || "",
        client_address: project.client_address || "",
        contractor_name: project.contractor_name || "Celsius",
        contractor_pan: project.contractor_pan || "",
        contractor_gstin: project.contractor_gstin || "",
        contractor_state: project.contractor_state || "",
        is_interstate: !!project.is_interstate,
        wo_attachment_id: project.wo_attachment_id || "",
        wo_notes: project.wo_notes || "",
      });
      setWoFile(null);
    }
  }, [open, project]);

  const uploadWoFile = async () => {
    if (!woFile || !project?.id) return null;
    setWoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", woFile);
      const { data } = await api.post(
        `/attachments/upload?parent_type=project&parent_id=${project.id}&category=client_po`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`WO file uploaded: ${woFile.name}`);
      setWoFile(null);
      return data.id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return null;
    } finally {
      setWoUploading(false);
    }
  };

  const [ocrBusy, setOcrBusy] = useState(false);
  const [extractedItems, setExtractedItems] = useState(null);
  const [extractedSplits, setExtractedSplits] = useState(null);
  const autoImportFromFile = async () => {
    if (!woFile) return toast.error("Pick a WO file first (PDF or image)");
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", woFile);
      const { data } = await api.post("/projects/parse-wo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = data.parsed || {};
      setForm((f) => ({
        ...f,
        work_order_no: p.work_order_no || f.work_order_no,
        work_order_date: p.work_order_date || f.work_order_date,
        work_order_value: p.work_order_value || f.work_order_value,
        agreement_no: p.agreement_no || f.agreement_no,
        name_of_work: p.name_of_work || f.name_of_work,
        sub_project: p.sub_project || f.sub_project,
        sub_head: p.sub_head || f.sub_head,
        site: p.site || f.site,
        client_name: p.client_name || f.client_name,
        client_address: p.client_address || f.client_address,
        client_gstin: p.client_gstin || f.client_gstin,
        client_state: p.client_state || f.client_state,
        contractor_name: p.contractor_name || f.contractor_name,
        contractor_pan: p.contractor_pan || f.contractor_pan,
        contractor_gstin: p.contractor_gstin || f.contractor_gstin,
        contractor_state: p.contractor_state || f.contractor_state,
        is_interstate: (typeof p.is_interstate === "boolean") ? p.is_interstate : f.is_interstate,
        wo_notes: p.wo_notes || f.wo_notes,
      }));
      setExtractedItems(data.items || []);
      setExtractedSplits(data.payment_splits || null);
      const filled = Object.values(p).filter((v) => v && String(v).trim() !== "" && v !== 0).length;
      const itemInfo = (data.items || []).length > 0 ? ` + ${data.items.length} BOQ item(s) ready to auto-create` : "";
      toast.success(`Extracted ${filled} field(s) from the WO${itemInfo} — review & save.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setOcrBusy(false);
    }
  };

  const createBoqFromExtracted = async () => {
    if (!extractedItems?.length) return toast.error("No BOQ items were extracted from the WO");
    if (!project?.id) return toast.error("Save the project first");
    const payload = {
      project_id: project.id,
      boq_title: form.name_of_work || form.name || "BOQ from WO",
      client_name: form.client_name,
      client_address: form.client_address,
      client_gstin: form.client_gstin,
      client_state: form.client_state,
      work_order_no: form.work_order_no,
      work_order_date: form.work_order_date,
      work_order_value: Number(form.work_order_value) || 0,
      agreement_no: form.agreement_no,
      name_of_work: form.name_of_work,
      sub_project: form.sub_project,
      sub_head: form.sub_head,
      site: form.site,
      contractor_name: form.contractor_name,
      contractor_pan: form.contractor_pan,
      contractor_gstin: form.contractor_gstin,
      contractor_state: form.contractor_state,
      is_interstate: !!form.is_interstate,
      default_supply_pct: extractedSplits?.supply_pct || 85,
      default_install_pct: extractedSplits?.install_pct || 10,
      default_tnc_pct: extractedSplits?.tnc_pct || 5,
      retention_pct: extractedSplits?.retention_pct || 5,
      tds_pct: extractedSplits?.tds_pct || 1,
      default_gst_rate: 18,
      items: extractedItems.map((it, i) => ({
        sr_no: it.sr_no || String(i + 1),
        section: "From Work Order",
        description: it.description,
        unit: it.unit || "Nos",
        hsn: it.hsn || "",
        contract_qty: Number(it.contract_qty) || 0,
        rate: Number(it.rate) || 0,
        amount: Number(it.amount) || 0,
        supply_pct: extractedSplits?.supply_pct || 85,
        install_pct: extractedSplits?.install_pct || 10,
        tnc_pct: extractedSplits?.tnc_pct || 5,
        gst_rate: Number(it.gst_rate) || 18,
        notes: "",
      })),
    };
    try {
      const { data: created } = await api.post("/boqs", payload);
      toast.success(`BOQ ${created.boq_no} created with ${created.items.length} item(s). Open the BOQ → RA Bills tab to view and edit.`);
      setExtractedItems(null);
      setExtractedSplits(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    if (!form.client_name.trim()) return toast.error("Client name is required");
    setSaving(true);
    try {
      let wo_attachment_id = form.wo_attachment_id || "";
      if (woFile) {
        const newAttId = await uploadWoFile();
        if (newAttId) wo_attachment_id = newAttId;
      }
      await api.put(`/projects/${project.id}`, {
        ...form,
        budget: Number(form.budget) || 0,
        work_order_value: Number(form.work_order_value) || 0,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        wo_attachment_id,
      });
      toast.success("Project updated · WO details will auto-flow to new BOQs / RA bills");
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[720px] sm:max-w-2xl lg:w-[min(880px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <PencilSimple size={18} className="text-[#EA580C]" /> Edit Project
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="basic" className="mt-5" data-testid="edit-project-tabs">
          <TabsList>
            <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
            <TabsTrigger value="wo" data-testid="tab-wo">Client Work Order</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="mt-4 space-y-4" data-testid="edit-project-form">
            <Field label="Project name">
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-project-name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client"><Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="edit-project-client" /></Field>
              <Field label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / site address" data-testid="edit-project-location" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Budget (₹)"><Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="edit-project-budget" className="font-mono-num" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="edit-project-start" /></Field>
              <Field label="End date"><Input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="edit-project-end" /></Field>
            </div>
            <Field label="Description / Scope">
              <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Notes visible to your team on the project detail page" data-testid="edit-project-description" />
            </Field>
          </TabsContent>

          <TabsContent value="wo" className="mt-4 space-y-4">
            <div className="border border-[#EA580C]/20 bg-[#EA580C]/5 rounded-sm p-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-1 flex items-center gap-1.5"><FileText size={12} /> Client Work Order</div>
              <div className="text-[11.5px] text-slate-600">These details auto-flow into new BOQs and RA Bills created under this project. Update once here — never re-type on every bill.</div>
            </div>

            {/* Upload + AI OCR panel — extracted (iter49) */}
            <WOOCRPanel
              woFile={woFile}
              setWoFile={setWoFile}
              ocrBusy={ocrBusy}
              existingAttachmentId={form.wo_attachment_id}
              onAutoImport={autoImportFromFile}
              extractedItems={extractedItems}
              extractedSplits={extractedSplits}
              onCreateBoq={createBoqFromExtracted}
              onDismissExtracted={() => setExtractedItems(null)}
            />

            {/* WO Core */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Work Order #"><Input value={form.work_order_no} onChange={(e) => set("work_order_no", e.target.value)} className="font-mono" data-testid="wo-no" placeholder="e.g. WO-2000004415" /></Field>
              <Field label="WO Date"><Input type="date" value={form.work_order_date || ""} onChange={(e) => set("work_order_date", e.target.value)} data-testid="wo-date" /></Field>
              <Field label="WO Value (₹)"><Input type="number" value={form.work_order_value} onChange={(e) => set("work_order_value", e.target.value)} className="font-mono-num text-right" data-testid="wo-value" /></Field>
              <Field label="Agreement No."><Input value={form.agreement_no} onChange={(e) => set("agreement_no", e.target.value)} className="font-mono" data-testid="wo-agreement" /></Field>
              <Field label="Name of Work"><Input value={form.name_of_work} onChange={(e) => set("name_of_work", e.target.value)} data-testid="wo-name-of-work" /></Field>
              <Field label="Site"><Input value={form.site} onChange={(e) => set("site", e.target.value)} data-testid="wo-site" /></Field>
              <Field label="Sub Project"><Input value={form.sub_project} onChange={(e) => set("sub_project", e.target.value)} data-testid="wo-sub-project" /></Field>
              <Field label="Sub Head"><Input value={form.sub_head} onChange={(e) => set("sub_head", e.target.value)} data-testid="wo-sub-head" /></Field>
              <Field label="Interstate?" hint="Sets IGST vs CGST+SGST default for RA bills">
                <Select value={form.is_interstate ? "yes" : "no"} onValueChange={(v) => set("is_interstate", v === "yes")}>
                  <SelectTrigger data-testid="wo-interstate"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="no">No · CGST + SGST</SelectItem><SelectItem value="yes">Yes · IGST</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>

            {/* Client */}
            <div className="border-t border-slate-200 pt-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Client Party</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Client GSTIN"><Input value={form.client_gstin} onChange={(e) => set("client_gstin", e.target.value)} className="font-mono" data-testid="wo-client-gstin" /></Field>
                <Field label="Client State"><Input value={form.client_state} onChange={(e) => set("client_state", e.target.value)} data-testid="wo-client-state" /></Field>
                <Field label="Client Address"><Input value={form.client_address} onChange={(e) => set("client_address", e.target.value)} data-testid="wo-client-address" /></Field>
              </div>
            </div>

            {/* Contractor */}
            <div className="border-t border-slate-200 pt-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Contractor Party (You)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Contractor"><Input value={form.contractor_name} onChange={(e) => set("contractor_name", e.target.value)} data-testid="wo-contractor" /></Field>
                <Field label="Contractor PAN"><Input value={form.contractor_pan} onChange={(e) => set("contractor_pan", e.target.value)} className="font-mono" data-testid="wo-contractor-pan" /></Field>
                <Field label="Contractor GSTIN"><Input value={form.contractor_gstin} onChange={(e) => set("contractor_gstin", e.target.value)} className="font-mono" data-testid="wo-contractor-gstin" /></Field>
                <Field label="Contractor State"><Input value={form.contractor_state} onChange={(e) => set("contractor_state", e.target.value)} data-testid="wo-contractor-state" /></Field>
              </div>
            </div>

            <Field label="WO Notes">
              <Textarea rows={2} value={form.wo_notes} onChange={(e) => set("wo_notes", e.target.value)} placeholder="Payment terms, special conditions, etc." data-testid="wo-notes" />
            </Field>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 pt-4 mt-4 border-t border-slate-200 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || woUploading || !form.name.trim() || !form.client_name.trim()}
            data-testid="save-edit-project"
            className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
          >
            <FloppyDisk size={14} className="mr-2" />
            {saving ? "Saving…" : (woUploading ? "Uploading…" : "Save changes")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
